import { useState } from "react";
import type { SanityProduct } from "../../../sanity/types";

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function AccordionItem({
  title,
  children,
  defaultOpen = false,
}: AccordionItemProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left cursor-pointer group"
      >
        <span className="text-sm font-semibold text-gray-800 group-hover:text-[#144E5D] transition-colors">
          {title}
        </span>
        <span
          className="w-5 h-5 flex items-center justify-center text-gray-400 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <i className="ri-arrow-down-s-line text-lg"></i>
        </span>
      </button>
      {open && (
        <div className="pb-5 text-sm text-gray-600 leading-relaxed animate-[fadeIn_0.2s_ease]">
          {children}
        </div>
      )}
    </div>
  );
}

interface ProductDetailsProps {
  product: SanityProduct;
}

export default function ProductDetails({ product }: ProductDetailsProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8">
      <h2 className="text-xl font-bold text-gray-900 mb-6">
        Product Information
      </h2>

      {/* Active Ingredients Table */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">
          Active Ingredients
        </h3>
        <div className="rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F0F7F8]">
              <tr>
                <th className="text-left py-2.5 px-4 font-semibold text-[#144E5D] text-xs uppercase tracking-wider">
                  Ingredient
                </th>
                <th className="text-left py-2.5 px-4 font-semibold text-[#144E5D] text-xs uppercase tracking-wider">
                  Purpose
                </th>
              </tr>
            </thead>
            <tbody>
              {product.activeIngredients.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 1 ? "bg-gray-50/60" : ""}>
                  <td className="py-3 px-4 font-medium text-gray-800">
                    {row.ingredient}
                  </td>
                  <td className="py-3 px-4 text-gray-600">{row.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accordion sections */}
      <div className="divide-y divide-gray-100 border-t border-gray-100">
        <AccordionItem title="Directions for Use" defaultOpen>
          <p>{product.directions}</p>
        </AccordionItem>

        <AccordionItem title="Inactive Ingredients">
          <p>{product.inactiveIngredients}</p>
        </AccordionItem>

        <AccordionItem title="Warnings & Safety Information">
          <ul className="space-y-2.5">
            {(product.warnings ?? []).map((w, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0 text-amber-500">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </AccordionItem>

        <AccordionItem title="Storage Instructions">
          <p>{product.storage}</p>
        </AccordionItem>
      </div>
    </div>
  );
}
